import {
  mapToDateString,
  reverseDDMMYYYY,
  reverseYYYYMMDD,
} from "Utils/dateUtils";
import {
  booleanToFormBoolean,
  formBooleanToBoolean,
  nullableBooleanToNullableFormBoolean,
} from "Utils/formUtils";
import { format, parse } from "date-fns";
import {
  disabilityPensionFormElementsActive,
  incomeDependencyOrDebtResponsibleFormElementsActive,
  workSituationFormElementsActive,
} from "./FormElements/activeFormElements";
import {
  AdviceProductDetailsBackendValues,
  AdviceProductDetailsFormValues,
  AdvisorValues,
} from "./FormElements/interfaces";

import { BooleanFormType } from "Store/interfaces/FormTypes";
import { isDefined } from "Utils/functions";
import { createKey } from "Utils/generalUtils";
import { AdviceProductIntroFormValues } from "../ProductIntro/interfaces";

const dateFormat = "yyyy-MM-dd";

export const mapAdviceDataToDetailsFormData = (
  adviceData: AdviceProductDetailsBackendValues,
): AdviceProductDetailsFormValues => {
  const {
    childDatesOfBirth,
    isExpectingChildren,
    incomeDependency,
    addCoInsured,
    coInsuredBirthDate,
    ...adviceBackendData
  } = adviceData;
  let adviceFormData: AdviceProductDetailsFormValues = adviceBackendData;
  if (childDatesOfBirth) {
    adviceFormData = {
      ...adviceFormData,
      childDatesOfBirth: childDatesOfBirth.map((date) => ({
        id: createKey(),
        dateOfBirth: reverseYYYYMMDD(mapToDateString(date)),
        inputFieldTouched: false,
        inputFieldError: false,
      })),
    };
  }
  if (isExpectingChildren !== undefined) {
    adviceFormData = {
      ...adviceFormData,
      isExpectingChildren: booleanToFormBoolean(isExpectingChildren),
    };
  }
  if (incomeDependency !== undefined) {
    adviceFormData = {
      ...adviceFormData,
      incomeDependency: booleanToFormBoolean(incomeDependency),
    };
  }
  if (adviceData.advisorValues) {
    adviceFormData = {
      ...adviceFormData,
      situationComment: adviceData.advisorValues.situationComment,
    };
  }
  if (adviceData.situationComment) {
    adviceFormData = {
      ...adviceFormData,
      situationComment: adviceData.situationComment,
    };
  }
  if (addCoInsured !== undefined) {
    adviceFormData = {
      ...adviceFormData,
      addCoInsured: booleanToFormBoolean(addCoInsured),
    };
  }
  if (coInsuredBirthDate !== undefined) {
    adviceFormData = {
      ...adviceFormData,
      coInsuredBirthDate: parse(
        mapToDateString(coInsuredBirthDate),
        dateFormat,
        new Date(),
      ),
    };
  }
  return adviceFormData;
};

export const mapDetailsFormDataToAdviceData = (
  adviceDetailsFormValues: AdviceProductDetailsFormValues,
  isAdvisor = false,
  advisorValues?: AdvisorValues,
): AdviceProductDetailsBackendValues => {
  const {
    childDatesOfBirth,
    isExpectingChildren,
    incomeDependency,
    isStayingInNorwayForMoreThanFiveYears,
    addCoInsured,
    coInsuredBirthDate,
    ...formRest
  } = cleanAdviceForm(adviceDetailsFormValues);

  const cleanedForm: AdviceProductDetailsBackendValues = formRest;

  if (isDefined(childDatesOfBirth)) {
    cleanedForm.childDatesOfBirth = childDatesOfBirth.map((child) =>
      reverseDDMMYYYY(child.dateOfBirth),
    );
  }
  if (isDefined(isExpectingChildren)) {
    cleanedForm.isExpectingChildren = formBooleanToBoolean(isExpectingChildren);
  }
  if (isDefined(incomeDependency)) {
    cleanedForm.incomeDependency = formBooleanToBoolean(incomeDependency);
  }
  if (isDefined(isStayingInNorwayForMoreThanFiveYears)) {
    cleanedForm.isStayingInNorwayForMoreThanFiveYears =
      isStayingInNorwayForMoreThanFiveYears;
  }
  if (isDefined(addCoInsured)) {
    cleanedForm.addCoInsured = formBooleanToBoolean(addCoInsured);
  }
  if (isDefined(coInsuredBirthDate)) {
    cleanedForm.coInsuredBirthDate = format(coInsuredBirthDate, dateFormat);
  }
  if (isAdvisor) {
    cleanedForm.isAdvisor = isAdvisor;
    if (isDefined(advisorValues)) {
      cleanedForm.advisorValues = advisorValues;
    }
  }

  return cleanedForm;
};

const cleanAdviceForm = (
  form: AdviceProductDetailsFormValues,
): AdviceProductDetailsFormValues => {
  const formValues: AdviceProductDetailsFormValues = {
    isStayingInNorwayForMoreThanFiveYears:
      form.isStayingInNorwayForMoreThanFiveYears,
    workSituation: form.workSituation,
    familySituation: form.familySituation,
    isExpectingChildren: form.isExpectingChildren,
    income: form.income,
    debt: form.debt,
    economicalSituation: form.economicalSituation,
    economicalHealthWish: form.economicalHealthWish,
  };
  if (isDefined(form.dateOfBirth)) {
    formValues.dateOfBirth = form.dateOfBirth;
  }
  if (workSituationFormElementsActive(formValues.workSituation)) {
    formValues.education = form.education;
  }
  if (
    formValues.debt > 0 &&
    incomeDependencyOrDebtResponsibleFormElementsActive(
      formValues.familySituation,
      formValues.debt,
    )
  ) {
    formValues.debtResponsible = form.debtResponsible;
  }
  if (disabilityPensionFormElementsActive(formValues.workSituation)) {
    formValues.disabilityPension = form.disabilityPension;
  }
  if (
    form.childDatesOfBirth !== undefined &&
    form.childDatesOfBirth.length > 0
  ) {
    formValues.childDatesOfBirth = form.childDatesOfBirth;
  }
  if (
    form.incomeDependency !== undefined &&
    formValues.income > 0 &&
    incomeDependencyOrDebtResponsibleFormElementsActive(
      formValues.familySituation,
      formValues.income,
    )
  ) {
    formValues.incomeDependency = form.incomeDependency;
  }
  if (form.addCoInsured !== undefined) {
    formValues.addCoInsured = form.addCoInsured;
  }
  if (
    form.addCoInsured === BooleanFormType.TRUE &&
    form.coInsuredBirthDate !== undefined
  ) {
    formValues.coInsuredBirthDate = form.coInsuredBirthDate;
  }
  if (form.situationComment !== undefined) {
    formValues.situationComment = form.situationComment;
  }
  return formValues;
};

export const mapAdviceDataToIntroFormData = (
  stayingInNorwayForMoreThanFiveYears?: boolean,
): AdviceProductIntroFormValues => {
  const isStayingInNorwayForMoreThanFiveYears =
    nullableBooleanToNullableFormBoolean(stayingInNorwayForMoreThanFiveYears);
  const isScandinavianCitizen =
    isStayingInNorwayForMoreThanFiveYears === BooleanFormType.TRUE
      ? BooleanFormType.FALSE
      : BooleanFormType.TRUE;
  return {
    isScandinavianCitizen,
    isStayingInNorwayForMoreThanFiveYears,
    pep: BooleanFormType.FALSE,
  };
};

export const mapAdviceIntroFormToIsResident = (
  formValues: AdviceProductIntroFormValues | undefined,
): boolean | undefined => {
  if (!isDefined(formValues)) {
    return undefined;
  }
  return !(
    formValues.isScandinavianCitizen === BooleanFormType.FALSE &&
    formValues.isStayingInNorwayForMoreThanFiveYears === BooleanFormType.FALSE
  );
};
